/**
 * "Edit price" dialog — updates the property's purchase price + ARV on
 * `property_setting` via `useDealSettings.saveSettings`.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MoneyInput } from "@/components/ui/money-input";
import { fmtMoney } from "@/lib/calculators";

const schema = z.object({
  purchase_price: z.coerce.number().min(0),
  after_repair_value: z.coerce.number().min(0),
});

type FormValues = z.input<typeof schema>;

interface EditPriceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchase: number;
  arv: number;
  isSaving: boolean;
  onSubmit: (values: { purchase_price: number; after_repair_value: number }) => void;
}

export function EditPriceDialog({
  open,
  onOpenChange,
  purchase,
  arv,
  isSaving,
  onSubmit,
}: EditPriceDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { purchase_price: purchase, after_repair_value: arv },
  });

  useEffect(() => {
    if (open) form.reset({ purchase_price: purchase, after_repair_value: arv });
  }, [open, purchase, arv, form]);

  const watchPurchase = Number(form.watch("purchase_price")) || 0;
  const watchArv = Number(form.watch("after_repair_value")) || 0;
  const equity = watchArv - watchPurchase;

  const handleSubmit = form.handleSubmit((values) => {
    onSubmit({
      purchase_price: Number(values.purchase_price),
      after_repair_value: Number(values.after_repair_value),
    });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit price</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="purchase_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase price</FormLabel>
                    <FormControl>
                      <MoneyInput
                        placeholder="245,000"
                        value={Number(field.value) || 0}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="after_repair_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>After-repair value</FormLabel>
                    <FormControl>
                      <MoneyInput
                        placeholder="285,000"
                        value={Number(field.value) || 0}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Estimated equity:{" "}
              <span className={equity >= 0 ? "text-success" : "text-destructive"}>
                {fmtMoney(equity)}
              </span>
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
